import React, { useState, useEffect } from 'react';
import { 
  FileText, Shield, Scale, Building, Phone, Mail, 
  ExternalLink, Printer, ChevronRight, CheckCircle2, 
  AlertCircle, Search, Lock, Clock, ArrowLeft, Download, Info
} from 'lucide-react';

interface LegalPageProps {
  initialDocument?: 'terms' | 'privacy';
  onNavigateHome: () => void;
  onNavigateTab: (tab: string) => void;
}

export const LegalPage: React.FC<LegalPageProps> = ({
  initialDocument = 'terms',
  onNavigateHome,
  onNavigateTab
}) => {
  const [activeDoc, setActiveDoc] = useState<'terms' | 'privacy'>(initialDocument);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setActiveDoc(initialDocument);
  }, [initialDocument]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeDoc]);

  const handlePrint = () => {
    window.print();
  };

  const handleSelectDoc = (doc: 'terms' | 'privacy') => {
    setActiveDoc(doc);
    const path = doc === 'terms' ? '/obshti-uslovia' : '/poveritelnost';
    if (window.location.pathname !== path) {
      window.history.pushState(null, '', path);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans pb-24">
      {/* Header Banner */}
      <div className="bg-[#0F1E32] text-white pt-36 pb-16 px-4 border-b border-[#C5A46E]/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1522] via-[#0F1E32] to-[#162B44] opacity-95"></div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-[#C5A46E]/10 blur-3xl pointer-events-none"></div>

        <div className="relative z-10 max-w-5xl mx-auto">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
            <button 
              onClick={onNavigateHome}
              className="hover:text-[#C5A46E] transition-colors cursor-pointer flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Начало</span>
            </button>
            <ChevronRight className="w-3 h-3 text-slate-500" />
            <span className="text-slate-300">Правна информация</span>
            <ChevronRight className="w-3 h-3 text-slate-500" />
            <span className="text-[#C5A46E] font-medium">
              {activeDoc === 'terms' ? 'Общи условия за ползване' : 'Политика за поверителност (GDPR)'}
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A46E]/15 border border-[#C5A46E]/30 text-[#C5A46E] text-xs font-bold uppercase tracking-widest mb-3">
                <Scale className="w-3.5 h-3.5" />
                <span>Правна документация • Варна Имотен Център</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white">
                {activeDoc === 'terms' ? 'Общи условия за ползване' : 'Защита на личните данни'}
              </h1>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl font-light">
                {activeDoc === 'terms' 
                  ? 'Официални правила за използване на уебсайта imotencentervarna.com и условия за посредническите услуги на „Имотен център Варна“ ЕООД.'
                  : 'Политика за поверителност, прозрачност и мерки за сигурност при обработване на лични данни съгласно Регламент (ЕС) 2016/679 (GDPR).'}
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={handlePrint}
                className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold flex items-center gap-2 border border-white/15 transition-all shadow-sm cursor-pointer"
                title="Печат на документа"
              >
                <Printer className="w-4 h-4 text-[#C5A46E]" />
                <span>Разпечатай / PDF</span>
              </button>
            </div>
          </div>

          {/* Document Switcher Tabs */}
          <div className="mt-8 flex flex-wrap gap-2 pt-4 border-t border-slate-700/60">
            <button
              onClick={() => handleSelectDoc('terms')}
              className={`px-5 py-3 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                activeDoc === 'terms'
                  ? 'bg-[#C5A46E] text-[#0F1E32] shadow-lg shadow-[#C5A46E]/20 scale-[1.02]'
                  : 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>1. Общи условия за ползване</span>
            </button>
            <button
              onClick={() => handleSelectDoc('privacy')}
              className={`px-5 py-3 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                activeDoc === 'privacy'
                  ? 'bg-[#C5A46E] text-[#0F1E32] shadow-lg shadow-[#C5A46E]/20 scale-[1.02]'
                  : 'bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10'
              }`}
            >
              <Shield className="w-4 h-4" />
              <span>2. Политика за поверителност (GDPR)</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Legal Content Container */}
      <div className="max-w-5xl mx-auto px-4 -mt-6 relative z-20">
        {/* Quick Agency Details Card */}
        <div className="bg-white rounded-2xl p-6 shadow-md border border-slate-200 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
          <div className="border-b md:border-b-0 md:border-r border-slate-100 pb-3 md:pb-0 md:pr-4">
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Търговец / Доставчик</span>
            <strong className="text-[#0F1E32] text-sm block">„Имотен център Варна“ ЕООД</strong>
            <span className="text-slate-500 block mt-0.5">ЕИК: 204164006</span>
            <span className="text-slate-500 block">Управител: Христо Бабаколов</span>
          </div>
          <div className="border-b md:border-b-0 md:border-r border-slate-100 pb-3 md:pb-0 md:pr-4">
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Адрес на управление</span>
            <p className="text-slate-700 font-medium">гр. Варна 9002, район „Приморски“, ул. „Ген. Колев“ № 113, ет. 3, офис 3.5</p>
          </div>
          <div className="border-b md:border-b-0 md:border-r border-slate-100 pb-3 md:pb-0 md:pr-4">
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Официални контакти</span>
            <a href="tel:0878859385" className="text-blue-700 hover:underline block font-semibold">0878 859 385</a>
            <a href="tel:0877002715" className="text-blue-700 hover:underline block font-semibold">0877 002 715</a>
            <a href="mailto:imotencentervarna@abv.bg" className="text-[#C5A46E] hover:underline block font-semibold mt-0.5">imotencentervarna@abv.bg</a>
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Правен статус</span>
            <div className="flex items-center gap-1.5 text-emerald-700 font-bold mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Действаща редакция</span>
            </div>
            <span className="text-slate-500 block text-[11px]">Дата: 22 октомври 2026 г.</span>
            <span className="text-slate-500 block text-[11px]">Приложимо право: Република България / ЕС</span>
          </div>
        </div>

        {/* Content Body */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-200">
          {activeDoc === 'terms' ? (
            <TermsContent />
          ) : (
            <PrivacyContent />
          )}
        </div>

        {/* Bottom Navigation & Help */}
        <div className="mt-8 bg-slate-100 rounded-2xl p-6 border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-[#C5A46E]" />
            <span className="text-slate-600">
              Имате правен въпрос или запитване относно имотните оферти?
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateTab('contacts')}
              className="px-4 py-2 bg-[#0F1E32] hover:bg-[#1C3759] text-white font-bold rounded-xl transition cursor-pointer"
            >
              Форма за контакти
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open_cookie_settings'))}
              className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 font-semibold border border-slate-300 rounded-xl transition cursor-pointer"
            >
              Настройки на бисквитки
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================================================================
   1. ОБЩИ УСЛОВИЯ ЗА ПОЛЗВАНЕ НА IMOTENCENTERVARNA.COM
   ========================================================================= */
const TermsContent: React.FC = () => {
  return (
    <article className="prose prose-slate max-w-none text-slate-700 text-sm leading-relaxed space-y-8">
      <div className="border-b border-slate-200 pb-6">
        <h2 className="text-2xl font-black text-[#0F1E32] uppercase tracking-tight m-0">
          ОБЩИ УСЛОВИЯ ЗА ПОЛЗВАНЕ НА IMOTENCENTERVARNA.COM
        </h2>
        <p className="text-xs text-slate-500 mt-2">
          Последна актуализация: <strong>22 октомври 2026 г.</strong> • В сила за всички посетители и потребители на сайта.
        </p>
      </div>

      {/* 1. Данни за доставчика */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">1</span>
          Данни за доставчика
        </h3>
        <p>
          Настоящите общи условия уреждат ползването на уебсайта <a href="https://imotencentervarna.com/" className="text-blue-600 hover:underline font-semibold">https://imotencentervarna.com/</a>, наричан по-долу „Сайта“, и условията за отправяне на запитвания към <strong>„Имотен център Варна“ ЕООД</strong>, наричано по-долу „Агенцията“.
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5 text-xs text-slate-700">
          <p><strong>Наименование:</strong> „Имотен център Варна“ ЕООД</p>
          <p><strong>ЕИК / БУЛСТАТ:</strong> 204164006</p>
          <p><strong>Седалище и адрес на управление:</strong> България, гр. Варна, п.к. 9002, район „Приморски“, ул. „Генерал Колев“ № 113, ет. 3, офис 3.5</p>
          <p><strong>Управител:</strong> Христо Людмилов Бабаколов</p>
          <p><strong>Електронна поща:</strong> <a href="mailto:imotencentervarna@abv.bg" className="text-blue-600 hover:underline">imotencentervarna@abv.bg</a></p>
          <p><strong>Телефони за контакт:</strong> <a href="tel:0878859385" className="text-blue-600 hover:underline">0878 859 385</a> и <a href="tel:0877002715" className="text-blue-600 hover:underline">0877 002 715</a></p>
          <p><strong>Официален уебсайт:</strong> <a href="https://imotencentervarna.com/" className="text-blue-600 hover:underline">https://imotencentervarna.com/</a></p>
        </div>
        <p className="text-xs text-slate-500">
          „Потребител“ по смисъла на настоящите условия е всяко лице, което посещава Сайта или използва неговите функционалности. Когато лицето има качеството „потребител“ по Закона за защита на потребителите, то се ползва от всички предвидени в закона права.
        </p>
      </section>

      {/* 2. Предназначение на Сайта */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">2</span>
          Предназначение на Сайта
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>2.1.</strong> Сайтът предоставя информация за недвижими имоти и за посредническите услуги на Агенцията и позволява отправяне на запитвания и заявки за контакт или оглед.</li>
          <li><strong>2.2.</strong> Разглеждането на обявите и изпращането на запитване са напълно безплатни.</li>
          <li><strong>2.3.</strong> Посещението на Сайта, разглеждането на конкретен имот или изпращането на заявка за оглед сами по себе си <strong>не представляват сключване на договор за посредничество</strong> и не пораждат задължение за заплащане на комисионна, неустойка или друга такса.</li>
          <li><strong>2.4.</strong> Платените посреднически услуги се възлагат с отделен договор, който определя техния обхват, възнаграждението, сроковете и правата и задълженията на страните.</li>
          <li><strong>2.5.</strong> Настоящите условия не заместват договора за посредничество, предварителния договор за покупко-продажба или нотариалния акт.</li>
        </ul>
      </section>

      {/* 3. Информация за недвижимите имоти */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">3</span>
          Информация за недвижимите имоти
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>3.1.</strong> Обявите могат да съдържат описание, цена, местоположение, площ, характеристики, снимки, видео и друга информация за предлагания имот.</li>
          <li><strong>3.2.</strong> Информацията се предоставя от собственици, техни представители и други оправомощени източници и може да се актуализира чрез използваните от Агенцията информационни системи.</li>
          <li><strong>3.3.</strong> Агенцията полага разумна грижа за точността и актуалността на обявите. Възможно е да има забавяне между промяна в статуса, цената или характеристиките на имот и отразяването ѝ в Сайта.</li>
          <li><strong>3.4.</strong> Преди оглед или предприемане на действия по сделка Агенцията потвърждава актуалната наличност и известните ѝ условия за съответната оферта.</li>
          <li><strong>3.5.</strong> Публикуваната обява има информационен характер и представлява покана за отправяне на запитване. Тя не е обвързващо предложение за сключване на сделка при всички посочени в нея условия.</li>
          <li><strong>3.6.</strong> Когато обозначението върху карта е приблизително, то служи за ориентация и не представлява потвърждение на точния адрес или границите на имота.</li>
          <li><strong>3.7.</strong> Визуализации, примерни разпределения и изображения с илюстративен характер се обозначават като такива. Обзавеждането и оборудването, показани на снимките, са включени в сделката само ако това е изрично уговорено.</li>
          <li><strong>3.8.</strong> Данните в обявата не заместват проверката на документите за собственост, тежестите, кадастралните данни, строителните книжа и други обстоятелства, които имат значение за сделката.</li>
        </ul>
      </section>

      {/* 4. Цени и допълнителни разходи */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">4</span>
          Цени и допълнителни разходи
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>4.1.</strong> Цените на имотите се публикуват с обозначение на валутата. Данъчното третиране и включването на ДДС, когато е приложимо, се уточняват за съответната оферта.</li>
          <li><strong>4.2.</strong> При приложимо законово изискване за двойно обозначаване на цените Агенцията ги представя съобразно действащите правила.</li>
          <li><strong>4.3.</strong> Посредническото възнаграждение не е включено в обявената цена на имота, освен ако изрично е посочено друго.</li>
          <li><strong>4.4.</strong> Освен цената на имота и евентуалното посредническо възнаграждение, сделката може да е свързана с местен данък, нотариални такси, такси за вписване, банкови разходи и други разходи според конкретния случай. Разпределението им се определя от закона и договореното между страните.</li>
          <li><strong>4.5.</strong> Преди възлагането на платена услуга Агенцията предоставя информация за крайната цена с включени приложими данъци или за начина на нейното изчисляване, както и за допълнителните разходи, ако има такива.</li>
        </ul>
      </section>

      {/* 5. Запитвания и заявки за оглед */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">5</span>
          Запитвания и заявки за оглед
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>5.1.</strong> Потребителят може да се свърже с Агенцията чрез публикуваните телефони, електронна поща или наличните форми в Сайта.</li>
          <li><strong>5.2.</strong> Потребителят следва да предоставя точни данни за контакт и информация, необходима за обработване на запитването.</li>
          <li><strong>5.3.</strong> Изпращането на заявка за оглед не означава автоматично потвърждение на дата и час. Огледът се счита за уговорен след потвърждение от представител на Агенцията.</li>
          <li><strong>5.4.</strong> Съгласно предоставените от Агенцията условия за посредничество при покупка <strong>не се дължат такси за извършени с нея огледи на територията на гр. Варна</strong>.</li>
          <li><strong>5.5.</strong> Евентуални разходи за оглед извън гр. Варна се съобщават предварително и се извършват за сметка на клиента само след неговото изрично съгласие.</li>
          <li><strong>5.6.</strong> При невъзможност да присъства на уговорен оглед Потребителят следва своевременно да уведоми Агенцията.</li>
          <li><strong>5.7.</strong> Изпращането на запитване или уговарянето на оглед не резервира имота и не задължава неговия собственик да преустанови предлагането му.</li>
        </ul>
      </section>

      {/* 6. Посреднически услуги */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">6</span>
          Посреднически услуги
        </h3>
        <p><strong>6.1.</strong> При сключване на договор за посредничество при покупка Агенцията може, според възложеното, да:</p>
        <ul className="list-disc pl-8 space-y-1 text-slate-600">
          <li>проучва пазара по зададени от клиента параметри;</li>
          <li>предлага подходящи имоти;</li>
          <li>организира огледи;</li>
          <li>съдейства за срещи и преговори със собствениците;</li>
          <li>координира подготовката на предварителен договор;</li>
          <li>съдейства за организиране на нотариалното изповядване на сделката.</li>
        </ul>
        <ul className="list-disc pl-5 space-y-1.5 pt-2">
          <li><strong>6.2.</strong> Правните консултации и действията, за които се изисква определена професионална правоспособност, се извършват от съответните правоспособни специалисти.</li>
          <li><strong>6.3.</strong> Конкретните услуги и евентуалните разходи за външни специалисти се уточняват с клиента предварително. Предложението на Агенцията за адвокат или нотариус не ограничава правото на клиента на избор при спазване на закона.</li>
          <li><strong>6.4.</strong> Агенцията не гарантира сключването на сделка, одобрение за кредит или приемането на определена цена от собственика.</li>
        </ul>
      </section>

      {/* 7. Посредническо възнаграждение при покупка */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">7</span>
          Посредническо възнаграждение при покупка
        </h3>
        <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-4 text-xs text-amber-950 space-y-2">
          <p>
            <strong>7.1.</strong> Предоставеният от Агенцията стандартен образец на договор за посредничество при покупка предвижда възнаграждение от <strong>3% от договорената покупна цена, но не по-малко от 1000 евро</strong>, с включени приложими данъци съгласно индивидуалния договор.
          </p>
          <p>
            <strong>7.2.</strong> Конкретното възнаграждение, включително приложимите данъци, основанието за неговото начисляване и моментът на плащане се посочват в отделния договор за посредничество, предоставен на клиента преди сключването му.
          </p>
        </div>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>7.3.</strong> Съгласно образеца възнаграждението се заплаща при подписване на предварителен договор за покупко-продажба на имот, за който е осъществено договореното посредничество. Ако предварителен договор не се сключва, плащането се извършва при подписване на окончателния договор във формата на нотариален акт.</li>
          <li><strong>7.4.</strong> Настоящата информация не поражда самостоятелно задължение за плащане. Такова задължение възниква при наличие на валидно договорно основание и изпълнение на приложимите условия.</li>
          <li><strong>7.5.</strong> Условията за посредничество при продажба, наем или други услуги се договарят отделно. Посоченото възнаграждение при покупка не се прилага автоматично за тях.</li>
        </ul>
      </section>

      {/* 8. Договори от разстояние и извън търговския обект */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">8</span>
          Договори от разстояние и извън търговския обект
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>8.1.</strong> Когато договор за посредническа услуга се сключва от разстояние или извън търговския обект и попада в приложното поле на Закона за защита на потребителите, Агенцията предоставя задължителната преддоговорна информация, условията за отказ и стандартния формуляр за отказ на траен носител.</li>
          <li><strong>8.2.</strong> В тези случаи потребителят има право да се откаже от договора за услугата в срок от <strong>14 дни от сключването му</strong>, без да посочва причина, при условията и изключенията, предвидени в закона.</li>
          <li><strong>8.3.</strong> Отказът може да бъде заявен с недвусмислено съобщение до <a href="mailto:imotencentervarna@abv.bg" className="text-blue-600 hover:underline">imotencentervarna@abv.bg</a> или на адреса на Агенцията. Използването на стандартния формуляр не е задължително. За спазване на срока е достатъчно съобщението да бъде изпратено преди неговото изтичане.</li>
          <li><strong>8.4.</strong> Изпълнение на платена услуга преди изтичане на срока за отказ започва само след изрично искане на потребителя и при спазване на приложимите законови изисквания.</li>
          <li><strong>8.5.</strong> При отказ след такова искане потребителят дължи само пропорционална сума за действително предоставената до уведомяването услуга, когато са изпълнени законовите предпоставки за това.</li>
          <li><strong>8.6.</strong> Правото на отказ при напълно изпълнена платена услуга се губи само при изпълнение на законовите условия, включително предварително изрично съгласие за започване на изпълнението и потвърждение, че потребителят знае за загубата на това право след пълното изпълнение.</li>
          <li><strong>8.7.</strong> При дължимо възстановяване на суми Агенцията го извършва в законовия срок, по правило до 14 дни от уведомяването за отказа, като отчита само законно дължимите суми.</li>
          <li><strong>8.8.</strong> Този раздел се отнася до посредническата услуга и не предоставя автоматично право на отказ от предварителен договор или от сключена сделка за недвижим имот.</li>
        </ul>
      </section>

      {/* 9. Правила за използване на Сайта */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">9</span>
          Правила за използване на Сайта
        </h3>
        <p><strong>9.1.</strong> Потребителите следва да използват Сайта законосъобразно и добросъвестно.</p>
        <p><strong>9.2.</strong> Не се допуска:</p>
        <ul className="list-disc pl-8 space-y-1 text-slate-600">
          <li>изпращане на неверни запитвания, спам или съдържание с незаконен характер;</li>
          <li>използване на чужди лични данни без правно основание;</li>
          <li>опити за неразрешен достъп до административни панели, профили или информационни системи;</li>
          <li>разпространяване на зловреден код или действия, които нарушават работата на Сайта;</li>
          <li>копиране и повторно публикуване на защитено съдържание в нарушение на правата на неговите носители.</li>
        </ul>
        <p><strong>9.3.</strong> При установена злоупотреба Агенцията може да предприеме съразмерни мерки за защита на Сайта, включително временно ограничаване на съответния достъп, без да засяга законните права на потребителите.</p>
      </section>

      {/* 10. Интелектуална собственост */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">10</span>
          Интелектуална собственост
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>10.1.</strong> Текстовете, снимките, видеоматериалите, логата, дизайнът и останалото защитено съдържание в Сайта принадлежат на Агенцията или се използват на законно основание.</li>
          <li><strong>10.2.</strong> Разрешено е разглеждането на съдържанието за лични информационни цели и споделянето на връзки към публичните страници на Сайта.</li>
          <li><strong>10.3.</strong> Използването на защитено съдържание за търговски цели, неговото копиране или повторно публикуване изисква разрешение от съответния носител на права, освен в изрично предвидените от закона случаи.</li>
          <li><strong>10.4.</strong> Настоящите условия не ограничават допустимото по закон използване на съдържание, включително цитиране.</li>
        </ul>
      </section>

      {/* 11. Лични данни и бисквитки */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">11</span>
          Лични данни и бисквитки
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>11.1.</strong> Агенцията обработва лични данни съобразно Регламент (ЕС) 2016/679, Закона за защита на личните данни и останалото приложимо законодателство.</li>
          <li><strong>11.2.</strong> Данните, предоставени при запитване или заявка за оглед, се обработват за отговор и за предприемане на действия по искане на лицето преди евентуално сключване на договор. В зависимост от конкретната дейност могат да се прилагат и други законови основания.</li>
          <li><strong>11.3.</strong> Изпращането на запитване и приемането на настоящите условия <strong>не представляват съгласие за получаване на рекламни съобщения</strong>.</li>
          <li><strong>11.4.</strong> Когато маркетинговата комуникация се основава на съгласие, то се предоставя отделно и доброволно и може да бъде оттеглено по всяко време.</li>
          <li><strong>11.5.</strong> Подробна информация за категориите данни, целите, правните основания, получателите, сроковете за съхранение и правата на лицата се предоставя в отделната <strong>Политика за поверителност</strong> на Сайта.</li>
          <li><strong>11.6.</strong> Използваните бисквитки и сходни технологии се описват в информацията за бисквитки. Когато законът изисква съгласие, съответните технологии се активират след предоставянето му.</li>
          <li><strong>11.7.</strong> Запитвания относно личните данни могат да се изпращат на <a href="mailto:imotencentervarna@abv.bg" className="text-blue-600 hover:underline">imotencentervarna@abv.bg</a>. Лицата имат право да сезират Комисията за защита на личните данни: <a href="https://cpdp.bg/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://cpdp.bg/</a>.</li>
        </ul>
      </section>

      {/* 12. Достъпност и отговорност */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">12</span>
          Достъпност и отговорност
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>12.1.</strong> Агенцията полага разумни усилия за нормалното функциониране на Сайта. Възможни са временни прекъсвания поради техническа поддръжка, актуализации или проблеми с използваните комуникационни услуги.</li>
          <li><strong>12.2.</strong> При установена неточност в обява Потребителят може да уведоми Агенцията чрез посочените контакти. Агенцията проверява сигнала и предприема необходимите действия.</li>
          <li><strong>12.3.</strong> Агенцията отговаря за собствените си действия и предоставените от нея услуги съобразно закона и сключените договори.</li>
          <li><strong>12.4.</strong> Нищо в настоящите условия не изключва или ограничава отговорност, която не може да бъде изключена или ограничена по закон, включително за умисъл, груба небрежност или нарушаване на задължителни потребителски права.</li>
          <li><strong>12.5.</strong> Когато Сайтът съдържа връзки към външни страници, за тях се прилагат условията на съответните доставчици. Това не освобождава Агенцията от отговорност за собствените ѝ задължения.</li>
        </ul>
      </section>

      {/* 13. Жалби и разрешаване на спорове */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">13</span>
          Жалби и разрешаване на спорове
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>13.1.</strong> Въпроси и жалби могат да се изпращат на <a href="mailto:imotencentervarna@abv.bg" className="text-blue-600 hover:underline">imotencentervarna@abv.bg</a> или на адреса на управление на Агенцията.</li>
          <li><strong>13.2.</strong> За улесняване на разглеждането им е препоръчително да съдържат име, контакт за обратна връзка, описание на случая и номер или връзка към офертата, ако е приложимо.</li>
          <li><strong>13.3.</strong> Агенцията разглежда жалбите в разумен срок, като спазва всички приложими законови срокове.</li>
          <li><strong>13.4.</strong> Потребителите могат да подадат жалба до Комисията за защита на потребителите чрез <a href="https://kzp.bg/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold">https://kzp.bg/</a> и да използват приложимите процедури за извънсъдебно решаване на потребителски спорове, включително чрез съответната помирителна комисия.</li>
          <li><strong>13.5.</strong> Възникналите спорове се решават чрез преговори, а при непостигане на съгласие — от компетентния съд, определен съгласно приложимите законови правила. Настоящите условия не ограничават предвидената от закона защита на потребителите относно компетентния съд.</li>
        </ul>
      </section>

      {/* 14. Промени и заключителни разпоредби */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">14</span>
          Промени и заключителни разпоредби
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>14.1.</strong> Агенцията може да актуализира настоящите условия при промяна в услугите, функционалностите на Сайта или приложимото законодателство.</li>
          <li><strong>14.2.</strong> Актуалната версия се публикува на тази страница с дата на последната промяна. Измененията не действат със задна дата и не променят автоматично вече сключени договори.</li>
          <li><strong>14.3.</strong> Отделният договор за посредничество урежда конкретно възложената услуга. При всички случаи се спазват задължителните разпоредби на закона и защитата срещу неравноправни клаузи.</li>
          <li><strong>14.4.</strong> Недействителността на отделна разпоредба не засяга останалите, доколкото те могат да се прилагат самостоятелно.</li>
          <li><strong>14.5.</strong> Прилага се българското законодателство, без да се засяга защитата, предоставена на потребителя от задължителни разпоредби на друго приложимо право.</li>
        </ul>
        <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span>Дата на публикуване: <strong>22 октомври 2026 г.</strong></span>
          <span>Гр. Варна, България</span>
        </div>
      </section>
    </article>
  );
};

/* =========================================================================
   2. ЗАЩИТА НА ЛИЧНИТЕ ДАННИ (ПОЛИТИКА ЗА ПОВЕРИТЕЛНОСТ)
   ========================================================================= */
const PrivacyContent: React.FC = () => {
  return (
    <article className="prose prose-slate max-w-none text-slate-700 text-sm leading-relaxed space-y-8">
      <div className="border-b border-slate-200 pb-6">
        <h2 className="text-2xl font-black text-[#0F1E32] uppercase tracking-tight m-0">
          ЗАЩИТА НА ЛИЧНИТЕ ДАННИ (ПОЛИТИКА ЗА ПОВЕРИТЕЛНОСТ)
        </h2>
        <p className="text-xs text-slate-500 mt-2">
          На уебсайта <a href="https://imotencentervarna.com/" className="text-blue-600 hover:underline">https://imotencentervarna.com/</a> • Последна актуализация: <strong>22 октомври 2026 г.</strong>
        </p>
      </div>

      <div className="bg-blue-50/70 border border-blue-200 rounded-2xl p-5 text-xs text-blue-950 space-y-2">
        <p className="font-semibold text-sm text-[#0F1E32]">
          „Имотен център Варна“ ЕООД зачита неприкосновеността на личния Ви живот.
        </p>
        <p>
          С настоящата политика обясняваме какви лични данни обработваме, за какви цели ги използваме, на кого можем да ги предоставяме и как можете да упражните правата си.
        </p>
        <p>
          Политиката се отнася до посетителите на нашия уебсайт, лицата, които отправят запитвания или заявяват огледи, клиентите на Агенцията и физическите лица, с които общуваме във връзка с посредническата си дейност.
        </p>
        <p className="text-slate-600">
          Обработването се извършва съобразно <strong>Регламент (ЕС) 2016/679 („GDPR“)</strong>, Закона за защита на личните данни и останалото приложимо законодателство на Република България и ЕС.
        </p>
      </div>

      {/* 1. Кой е администраторът на личните данни? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">1</span>
          Кой е администраторът на личните данни?
        </h3>
        <p>Администратор е:</p>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1.5 text-xs text-slate-700">
          <p><strong>„Имотен център Варна“ ЕООД</strong></p>
          <p><strong>ЕИК:</strong> 204164006</p>
          <p><strong>Седалище и адрес на управление:</strong> България, гр. Варна, п.к. 9002, район „Приморски“, ул. „Генерал Колев“ № 113, ет. 3, офис 3.5</p>
          <p><strong>Управител:</strong> Христо Людмилов Бабаколов</p>
          <p><strong>Електронна поща:</strong> <a href="mailto:imotencentervarna@abv.bg" className="text-blue-600 hover:underline">imotencentervarna@abv.bg</a></p>
          <p><strong>Телефони:</strong> 0878 859 385 и 0877 002 715</p>
          <p><strong>Уебсайт:</strong> <a href="https://imotencentervarna.com/" className="text-blue-600 hover:underline">https://imotencentervarna.com/</a></p>
        </div>
        <p className="text-xs text-slate-500">
          В настоящата политика използваме също наименованията „Агенцията“, „ние“ и „нас“. За въпроси относно личните Ви данни и за упражняване на права можете да използвате посочения имейл или пощенски адрес.
        </p>
      </section>

      {/* 2. Какво означават използваните понятия? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">2</span>
          Какво означават използваните понятия?
        </h3>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Лични данни:</strong> всяка информация, чрез която физическо лице може да бъде идентифицирано пряко или косвено — например име, телефон, имейл или онлайн идентификатор.</li>
          <li><strong>Обработване:</strong> всяка операция или съвкупност от операции, извършвани с лични данни — като събиране, записване, организиране, използване, предоставяне, съхраняване и изтриване.</li>
          <li><strong>Администратор:</strong> организацията („Имотен център Варна“ ЕООД), която определя целите и средствата за обработване на личните данни.</li>
          <li><strong>Обработващ лични данни:</strong> физическо или юридическо лице, доставчик или орган, който обработва лични данни от името на администратора и съобразно неговите указания.</li>
        </ul>
      </section>

      {/* 3. Чии данни обработваме? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">3</span>
          Чии данни обработваме?
        </h3>
        <p>В рамките на описаните дейности можем да обработваме данни на:</p>
        <ul className="list-disc pl-5 space-y-1 text-slate-600">
          <li>посетители на Сайта;</li>
          <li>лица, които изпращат запитвания или заявяват оглед;</li>
          <li>купувачи, продавачи, наематели и наемодатели;</li>
          <li>собственици, съсобственици и упълномощени представители;</li>
          <li>представители и лица за контакт на бизнес партньори;</li>
          <li>лица, които подават жалби или искания относно личните си данни;</li>
          <li>лица, заявили желание да получават маркетингови съобщения.</li>
        </ul>
      </section>

      {/* 4. Какви лични данни обработваме? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">4</span>
          Какви лични данни обработваме?
        </h3>
        <p>Обемът на данните зависи от конкретното Ви взаимодействие с нас. Не изискваме всички изброени данни от всеки посетител.</p>
        
        <div className="space-y-4 pt-1">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h4 className="font-bold text-sm text-[#0F1E32] mb-1">4.1. При запитване и организиране на оглед</h4>
            <p className="text-xs text-slate-600 mb-2">Обработваме само най-необходимите данни за връзка:</p>
            <ul className="list-disc pl-5 text-xs text-slate-600 space-y-0.5">
              <li>име и фамилия;</li>
              <li>телефонен номер и адрес на електронна поща;</li>
              <li>съдържание на запитването и последващата кореспонденция;</li>
              <li>номер на оферта и имот, към който проявявате интерес;</li>
              <li>предпочитания за район, вид имот, бюджет и други критерии;</li>
              <li>уговорени дата и час за провеждане на оглед.</li>
            </ul>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h4 className="font-bold text-sm text-[#0F1E32] mb-1">4.2. При възлагане на посредническа услуга (договор)</h4>
            <p className="text-xs text-slate-600 mb-2">Когато е необходимо за договора, сделката или изпълнение на законово задължение, можем да обработваме допълнително:</p>
            <ul className="list-disc pl-5 text-xs text-slate-600 space-y-0.5">
              <li>пълни имена, адрес и идентификационни данни;</li>
              <li>ЕГН или друг приложим идентификатор (съгласно закона);</li>
              <li>данни от документ за самоличност (само при нотариална сделка или проверка по ЗМИП);</li>
              <li>данни за представителна власт и пълномощни;</li>
              <li>договори, протоколи за оглед и свързана кореспонденция;</li>
              <li>фактурни данни, банкова сметка и информация за плащания;</li>
              <li>информация за собствеността и документи за имота.</li>
            </ul>
            <p className="text-[11px] text-amber-800 bg-amber-50 p-2 rounded-lg mt-2 border border-amber-200">
              💡 За обикновено запитване през Сайта <strong>не е необходимо да изпращате ЕГН, копие на лична карта или банкови данни</strong>.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h4 className="font-bold text-sm text-[#0F1E32] mb-1">4.3. При публикуване на имот</h4>
            <p className="text-xs text-slate-600">
              Обработваме описания, снимки, видео и местоположение. Преди публикуване се премахват видими лични документи, семейни снимки и лица. Контактите и документите на собственика не се публикуват публично в обявата.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <h4 className="font-bold text-sm text-[#0F1E32] mb-1">4.4. При посещение на Сайта (технически данни)</h4>
            <ul className="list-disc pl-5 text-xs text-slate-600 space-y-0.5">
              <li>IP адрес (анонимизиран при анализи);</li>
              <li>дата и час на заявката;</li>
              <li>посетен адрес в Сайта;</li>
              <li>тип браузър и операционна система;</li>
              <li>технически записи за сигурност (защита от ботове и спам през Cloudflare);</li>
              <li>записан избор относно съгласието за бисквитките.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 5. Откъде получаваме данните? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">5</span>
          Откъде получаваме данните?
        </h3>
        <p>Получаваме данни:</p>
        <ul className="list-disc pl-5 space-y-1 text-slate-600">
          <li>непосредствено от Вас — чрез форми за контакт на Сайта, имейл, телефон, лични срещи и документи;</li>
          <li>от Ваш изрично упълномощен представител;</li>
          <li>от участници в конкретна сделка, когато предоставянето е необходимо и законосъобразно;</li>
          <li>от публични регистри (Агенция по вписванията, Кадастър), когато проверката е необходима за сделката;</li>
          <li>от използваните информационни системи и технически доставчици.</li>
        </ul>
      </section>

      {/* 6. За какви цели и на какви основания използваме данните? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">6</span>
          За какви цели и на какви основания използваме данните?
        </h3>
        <p>Обработването се основава на изрично предвидените в GDPR правни основания:</p>

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#0F1E32] text-white uppercase text-[11px] tracking-wider">
              <tr>
                <th className="p-3">Цел на обработването</th>
                <th className="p-3">Правно основание по GDPR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Отговор на запитване, подбор на оферти по Ваше искане и организиране на оглед</td>
                <td className="p-3 text-slate-600">Предприемане на стъпки по Ваше искане преди сключване на договор — <strong>чл. 6, пар. 1, б. „б“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Изпълнение на възложена посредническа услуга</td>
                <td className="p-3 text-slate-600">Изпълнение на договор — <strong>чл. 6, пар. 1, б. „б“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Счетоводно отчитане, данъчни задължения и проверки по законодателството срещу изпирането на пари (ЗМИП)</td>
                <td className="p-3 text-slate-600">Законово задължение на Агенцията — <strong>чл. 6, пар. 1, б. „в“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Защита на Сайта, предотвратяване на злоупотреби и поддържане на техническата сигурност</td>
                <td className="p-3 text-slate-600">Легитимен интерес на Агенцията — <strong>чл. 6, пар. 1, б. „е“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Установяване, упражняване или защита на правни претенции</td>
                <td className="p-3 text-slate-600">Легитимен интерес — <strong>чл. 6, пар. 1, б. „е“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Служебна комуникация с представители на партньори и клиенти — юридически лица</td>
                <td className="p-3 text-slate-600">Легитимен интерес за осъществяване на деловите отношения — <strong>чл. 6, пар. 1, б. „е“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Изпращане на маркетингови съобщения и бюлетин (при изрично доброволно записване)</td>
                <td className="p-3 text-slate-600">Изрично съгласие — <strong>чл. 6, пар. 1, б. „а“ GDPR</strong></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Незадължителни аналитични и рекламни технологии (бисквитки)</td>
                <td className="p-3 text-slate-600">Предварително съгласие през банера за бисквитки — <strong>чл. 6, пар. 1, б. „а“ GDPR</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 7. Задължително ли е предоставянето на данни? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">7</span>
          Задължително ли е предоставянето на данни?
        </h3>
        <p>Разглеждането на публичните обяви не изисква предоставяне на име или телефон.</p>
        <p>За да отговорим на запитване, е необходимо да разполагаме с подходящ контакт. Ако той липсва, може да не успеем да Ви отговорим или да организираме оглед.</p>
        <p>Данните, необходими за договор или законова проверка, се уточняват при съответната услуга. Съгласието за маркетинг е изцяло доброволно и не е условие за получаване на посредническа услуга.</p>
      </section>

      {/* 8. На кого можем да предоставяме данни? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">8</span>
          На кого можем да предоставяме данни?
        </h3>
        <p>Предоставяме само необходимите данни и при наличие на правно основание на:</p>
        <ul className="list-disc pl-5 space-y-1 text-slate-600">
          <li>упълномощени брокери, служители и представители на Агенцията;</li>
          <li>доставчици на сигурен хостинг, техническа поддръжка, електронна поща (Cloudflare, Resend) и софтуер за управление на имоти (Estate Assistant);</li>
          <li>счетоводни и правни консултанти;</li>
          <li>нотариуси, банки и кредитни консултанти — само по конкретна сделка и с Вашето знание и съгласие;</li>
          <li>компетентни държавни органи при надлежно законово задължение.</li>
        </ul>
      </section>

      {/* 9. Предаване на данни извън ЕИП */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">9</span>
          Предаване на данни извън Европейското икономическо пространство
        </h3>
        <p>
          При използване на международни технически доставчици (напр. инфраструктура за сигурност Cloudflare) личните данни се защитават съгласно изискванията на GDPR чрез стандартни договорни клаузи (SCC) на Европейската комисия или приложими решения за адекватност.
        </p>
      </section>

      {/* 10. Колко дълго съхраняваме данните? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">10</span>
          Колко дълго съхраняваме данните?
        </h3>
        <p>Съхраняваме данните за срока, необходим за съответната цел:</p>

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#0F1E32] text-white uppercase text-[11px] tracking-wider">
              <tr>
                <th className="p-3">Категория данни</th>
                <th className="p-3">Срок за съхранение</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Запитвания през сайта, които не водят до договор</td>
                <td className="p-3 text-slate-600">До 12 месеца от последната съдържателна комуникация</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Договори за посредничество и доказателства за изпълнение</td>
                <td className="p-3 text-slate-600">5 години след прекратяване на отношенията (съгласно общия давностен срок по ЗЗД)</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Счетоводни и данъчни документи и фактури</td>
                <td className="p-3 text-slate-600">10 години съгласно Закона за счетоводството и ДОПК</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Документи по законодателството срещу изпирането на пари (ЗМИП)</td>
                <td className="p-3 text-slate-600">5 години от датата на извършване на съответната сделка съгласно ЗМИП</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Контакти за маркетинг и новини</td>
                <td className="p-3 text-slate-600">До оттегляне на съгласието или максимум до 24 месеца</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Технически логове за сигурност</td>
                <td className="p-3 text-slate-600">До 90 дни, освен при инцидент за сигурност</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="p-3 font-medium">Бисквитки и идентификатори</td>
                <td className="p-3 text-slate-600">Според срока на конкретната бисквитка (посочен в Центъра за бисквитки)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 11. Какви права имате по GDPR? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">11</span>
          Какви права имате по GDPR?
        </h3>
        <p>Като субект на данни съгласно GDPR имате следните неотменими права:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-[#0F1E32] block mb-1">1. Право на достъп</strong>
            <span>Да получите потвърждение дали обработваме Ваши данни и безплатно копие от тях.</span>
          </div>
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-[#0F1E32] block mb-1">2. Право на коригиране</strong>
            <span>Да поискате коригиране на неточни или допълване на непълни данни.</span>
          </div>
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-[#0F1E32] block mb-1">3. Право на изтриване („Да бъдете забравен“)</strong>
            <span>При отпадане на основанието (освен при законово задължение за пазене).</span>
          </div>
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-[#0F1E32] block mb-1">4. Право на ограничаване</strong>
            <span>Временно спиране на обработването при оспорване на точността им.</span>
          </div>
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-[#0F1E32] block mb-1">5. Право на преносимост</strong>
            <span>Да получите данните си в структуриран и четим машинен формат.</span>
          </div>
          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <strong className="text-[#0F1E32] block mb-1">6. Право на възражение</strong>
            <span>По всяко време срещу обработване на основание легитимен интерес или директен маркетинг.</span>
          </div>
        </div>
      </section>

      {/* 12. Как да упражните правата си? */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">12</span>
          Как да упражните правата си?
        </h3>
        <p>
          Изпратете писмено искане на електронна поща: <a href="mailto:imotencentervarna@abv.bg" className="text-blue-600 hover:underline font-bold">imotencentervarna@abv.bg</a> или на пощенския адрес на Агенцията: гр. Варна, ул. „Ген. Колев“ № 113, ет. 3, офис 3.5.
        </p>
        <p>
          Не е задължително да използвате специален формуляр. Отговаряме без ненужно забавяне в срок до <strong>един месец</strong> от получаване на искането. Упражняването на правата е напълно <strong>безплатно</strong>.
        </p>
      </section>

      {/* 13-16: Маркетинг, Бисквитки, Сигурност */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">13-16</span>
          Маркетинг, бисквитки, автоматизирани решения и сигурност
        </h3>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Маркетинг:</strong> Изпращането на запитване не представлява съгласие за рекламни съобщения. Маркетинг се изпраща само при изрично съгласие, което можете да оттеглите по всяко време.</li>
          <li><strong>Бисквитки:</strong> Незадължителни аналитични и рекламни бисквитки се активират само след съгласие. Можете да промените избора си по всяко време от бутона „Бисквитки & GDPR“ в долната част на сайта.</li>
          <li><strong>Автоматизирани решения:</strong> В рамките на услугите не се вземат решения, основани единствено на автоматизирано обработване или профилиране с правни последици.</li>
          <li><strong>Сигурност:</strong> Агенцията прилага надеждни технически мерки — SSL криптиране, ограничен достъп и защитени протоколи за обмен.</li>
        </ul>
      </section>

      {/* 17. Право на жалба до надзорен орган */}
      <section className="space-y-3">
        <h3 className="text-lg font-bold text-[#0F1E32] flex items-center gap-2">
          <span className="w-6 h-6 rounded-lg bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center text-xs font-black">17</span>
          Право на жалба до надзорен орган
        </h3>
        <p>
          Ако считате, че обработването на личните Ви данни нарушава приложимото законодателство, имате право да подадете жалба директно до надзорния орган:
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-1 text-slate-800">
          <strong className="text-sm text-[#0F1E32] block">Комисия за защита на личните данни (КЗЛД)</strong>
          <p><strong>Адрес за кореспонденция:</strong> гр. София 1592, бул. „Проф. Цветан Лазаров“ № 2</p>
          <p><strong>Телефон:</strong> 02 915 35 18</p>
          <p><strong>Електронна поща:</strong> <a href="mailto:kzld@cpdp.bg" className="text-blue-600 hover:underline">kzld@cpdp.bg</a></p>
          <p><strong>Интернет страница:</strong> <a href="https://cpdp.bg/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold">https://cpdp.bg/</a></p>
        </div>
      </section>

      {/* 18. Промени в политиката */}
      <section className="space-y-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
        <h3 className="text-base font-bold text-[#0F1E32]">18. Промени в политиката</h3>
        <p>
          Актуалната версия се публикува на тази страница с дата на последната промяна (22 октомври 2026 г.).
        </p>
        <p>
          Официален контакт по въпроси за защита на данните: <a href="mailto:imotencentervarna@abv.bg" className="text-[#C5A46E] hover:underline font-bold">imotencentervarna@abv.bg</a>.
        </p>
      </section>
    </article>
  );
};
